/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react-hooks/rules-of-hooks */
import { ConnectForm, ControlledComponentProps, ErrorMessages, Label, Text } from "@/components"
import { useStyles } from "@/components/Controlled/QuorumRichTextarea/QuorumRichTextarea.style"
import { Link, RichTextEditor } from "@mantine/tiptap"
import type { Editor } from "@tiptap/core"
import { Color } from "@tiptap/extension-color"
import { Highlight } from "@tiptap/extension-highlight"
import type { MentionOptions } from "@tiptap/extension-mention"
import { Mention } from "@tiptap/extension-mention"
import { Placeholder } from "@tiptap/extension-placeholder"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { TextAlign } from "@tiptap/extension-text-align"
import { TextStyle } from "@tiptap/extension-text-style"
import { Underline } from "@tiptap/extension-underline"
import type { Transaction } from "@tiptap/pm/state"
import { useEditor } from "@tiptap/react"
import { StarterKit } from "@tiptap/starter-kit"
import React from "react"
import { ControllerRenderProps, FieldValues, useController } from "react-hook-form"
import { Image } from "@tiptap/extension-image"
import {
    RichTextCodeView,
    CodeViewContent,
} from "@/components/Controlled/QuorumRichTextarea/RichTextCodeView/RichTextCodeView"
import { FontStylesExtension } from "@/components/Controlled/QuorumRichTextarea/extensions"

type QuorumRichTextareaProps = {
    name: string
    label: string
    mentionConfig: Partial<MentionOptions>
    tooltip?: string
    description?: string
    placeholder?: string
    formatContent?: (value: string) => string
    renderToolbar?: (editor: Editor) => React.ReactNode
    onUpdate?: (props: {
        editor: Editor
        transaction: Transaction
        field: ControllerRenderProps<FieldValues, string>
    }) => void
}

const CustomHighlight = Highlight.extend({
    renderHTML({ HTMLAttributes }) {
        return ["mark", { ...HTMLAttributes, style: "color: inherit" }, 0]
    },
})

const CustomLink = Link.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            target: {
                default: null,
                parseHTML: (element) => element.getAttribute("target"),
                renderHTML: (attributes) => (attributes.target === "_blank" ? { target: "_blank" } : {}),
            },
        }
    },

    renderHTML({ HTMLAttributes }) {
        const attributes = { ...HTMLAttributes }

        if (attributes.target !== "_blank") {
            delete attributes.target
        }

        return ["a", attributes, 0]
    },
})

export const QuorumRichTextarea = ({
    name,
    defaultValue,
    formatContent,
    rules,
    mentionConfig,
    renderToolbar,
    onUpdate,
    label,
    tooltip,
    description,
    placeholder,
    shouldUnregister,
}: ControlledComponentProps<QuorumRichTextareaProps>) => (
    <ConnectForm>
        {({ control }) => {
            const { field, fieldState } = useController({
                name,
                control,
                defaultValue,
                rules,
                shouldUnregister,
            })

            const [hasSetContent, setHasSetContent] = React.useState(false)

            const editor = useEditor({
                extensions: [
                    Color,
                    CustomHighlight,
                    Image,
                    CustomLink,
                    Mention.configure(mentionConfig),
                    Placeholder.configure({ placeholder }),
                    RichTextCodeView.configure({ onChange: field.onChange }),
                    StarterKit,
                    Subscript,
                    Superscript,
                    TextAlign.configure({ types: ["heading", "paragraph"] }),
                    TextStyle,
                    Underline,
                    FontStylesExtension,
                ],
                content: defaultValue ?? field.value,
                onUpdate: onUpdate ? (props) => onUpdate({ ...props, field }) : field.onChange,
                onBlur: field.onBlur,
            })

            // field.value will first be a null value before the form data is loaded,
            // this check ensures we do a reset of initial value without it infinitely reset
            // don't use useEffect here because it creates lagging when typing
            if (
                editor &&
                !hasSetContent &&
                field.value &&
                formatContent &&
                formatContent(field.value) !== editor.getHTML()
            ) {
                editor.commands.setContent(formatContent(field.value))
                setHasSetContent(true)
            }

            const { classes } = useStyles({ error: fieldState.error })
            const isCodeViewEnabled = editor?.extensionStorage.codeView.enabled
            return (
                <div className={classes.container}>
                    <Label label={label} tooltip={tooltip} withAsterisk={!!rules?.required} className={classes.label} />
                    <RichTextEditor data-cy={name} editor={editor}>
                        {renderToolbar?.(editor)}
                        {isCodeViewEnabled ? <CodeViewContent editor={editor} /> : <RichTextEditor.Content id={name} />}
                    </RichTextEditor>
                    {description && <Text>{description}</Text>}
                    {fieldState.error && <ErrorMessages fieldError={fieldState.error} />}
                </div>
            )
        }}
    </ConnectForm>
)
